import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Info, Menu } from 'lucide-react';
import SectionCard from '../SectionCard';
import { useRoleEditorContext } from '../RoleEditorContext';
import { SOVEREIGN } from '../../../../epc/utils/epcProjectVisual';

/**
 * Step 6 of 6 — Menu order (optional).
 *
 * Sidebar customisation now lives on the dedicated Menu Configuration page
 * (/admin/roles/:roleId/menu). This section summarises the current state and
 * links there; for unsaved (new) roles it directs the user to save first.
 */
export default function MenuOrderSection() {
  const { editingRole } = useRoleEditorContext();
  const navigate = useNavigate();

  const customCount = editingRole?.menuOrder?.menuItems?.length ?? 0;
  const hasCustom = customCount > 0;
  const roleId = (editingRole?.id || editingRole?._id) ?? '';
  const canCustomise = !!roleId;

  const summaryText = hasCustom
    ? `Custom order — ${customCount} item${customCount === 1 ? '' : 's'}`
    : 'Default order';

  return (
    <SectionCard
      icon={Menu}
      title="Menu order"
      sub="Sidebar customisation for users with this role."
      step={6}
      totalSteps={6}
    >
      <div
        className="flex flex-wrap items-center justify-between gap-3 rounded-xl px-4 py-3"
        style={{
          border: `1px solid ${SOVEREIGN.cardBorder}`,
          background: SOVEREIGN.inputBg,
        }}
      >
        <div className="flex items-center gap-3">
          <span
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
            style={{
              backgroundColor: 'rgba(201,169,97,0.15)',
              color: SOVEREIGN.bottle,
            }}
          >
            Optional
          </span>
          <div className="text-sm">
            <p className="font-medium" style={{ color: SOVEREIGN.ink }}>{summaryText}</p>
            <p className="text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>
              Reorder, hide and re-icon sidebar entries on the dedicated Menu Configuration page.
            </p>
          </div>
        </div>

        {canCustomise ? (
          <button
            type="button"
            onClick={() =>
              navigate(`/admin/roles/${roleId}/menu`, { state: { role: editingRole } })
            }
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] font-semibold text-white shadow-sm transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-1"
            style={{ background: SOVEREIGN.btn }}
          >
            <Menu size={14} />
            Open Menu Configuration
            <ArrowUpRight size={14} />
          </button>
        ) : (
          <span
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-medium"
            style={{
              border: `1px solid ${SOVEREIGN.champagne}`,
              background: 'rgba(201,169,97,0.08)',
              color: SOVEREIGN.bottle,
            }}
          >
            <Info size={12} />
            Save the role first to enable customisation
          </span>
        )}
      </div>
    </SectionCard>
  );
}
